import { Component, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';

/**
 * FE-11-54 — a render error should cost the user one screen, not the whole app with
 * no way back. Reloading is offered because the most common cause is a stale bundle
 * after a deploy.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sentry hooks in here (§10 observability).
    // eslint-disable-next-line no-console
    console.error('Unhandled render error', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-4">
        <main className="card w-full max-w-md p-7 text-center" role="alert">
          <h1 className="text-base font-semibold text-ink">Something went wrong</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            This screen ran into an unexpected error. Reloading usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-primary mt-5"
          >
            Reload
          </button>
        </main>
      </div>
    );
  }
}

/** FE-11-54 — a passive offline indicator, so a failed save has an explanation. */
export function OfflineBanner() {
  const online = useOnlineStatus();
  if (online) return null;

  return (
    <div
      className="sticky top-0 z-50 bg-[#e0796b] px-4 py-1.5 text-center text-xs font-semibold text-[#0F1319]"
      role="status"
    >
      You&rsquo;re offline. Your answers will not save until the connection returns.
    </div>
  );
}

function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  return online;
}
